import { useState, type FormEvent } from 'react';
import type { FilterNode, SortSpec } from '@aurora/contracts';
import type { ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Button, Field, Input, Textarea } from '../../components/ui';
import { Dialog, useConfirm } from '../../components/dialog';
import { useToast } from '../../components/toast';
import { asFilterNode } from './list-filters';
import { useViewMutations, type SavedView } from './views-api';

/**
 * Saving the list as it stands, and renaming a view.
 *
 * Errors stay in the dialog, beside the field they are about, with what was
 * typed kept: a name that is already taken is the usual one, and being sent
 * back to an empty form to try again is how people give up on a feature.
 */

/** What the list is showing right now, ready to be stored as a view. */
export interface ViewSnapshot {
  filter: FilterNode | null;
  sort: SortSpec[];
  searchQuery: string | null;
  columns: string[];
}

function nameError(error: ApiError, name: string): string | null {
  // The one unique rule on a view is its name, per owner.
  if (error.code === 'conflict' || error.code === 'duplicate') return `You already have a view called “${name}”.`;
  return error.fieldErrors.name ?? null;
}

export function SaveViewDialog({
  open,
  onOpenChange,
  snapshot,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshot: ViewSnapshot;
  onSaved: (id: number) => void;
}) {
  const { can } = useSession();
  const toast = useToast();
  const { create, pin } = useViewMutations();
  const canPersonal = can('view.create_personal');
  const canShared = can('view.create_shared');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'shared'>(canPersonal ? 'private' : 'shared');
  const [pinIt, setPinIt] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!trimmed) {
      setError('Give the view a name.');
      return;
    }
    setError(null);
    create.mutate(
      {
        name: trimmed,
        description: description.trim() || null,
        visibility,
        ...(snapshot.filter ? { filter: snapshot.filter } : {}),
        ...(snapshot.sort.length > 0 ? { sort: snapshot.sort } : {}),
        ...(snapshot.searchQuery ? { search_query: snapshot.searchQuery } : {}),
        columns: snapshot.columns,
      },
      {
        onSuccess: (response) => {
          const id = response.data.id;
          if (pinIt) pin.mutate({ id, pinned: true }, { onError: (failure) => toast.error(failure) });
          toast.success(`Saved “${trimmed}”`, pinIt ? 'It is pinned to the tabs above the list.' : undefined);
          setName('');
          setDescription('');
          onOpenChange(false);
          onSaved(id);
        },
        onError: (failure) => {
          const inline = nameError(failure, trimmed);
          if (inline) setError(inline);
          else toast.error(failure);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setError(null);
        onOpenChange(next);
      }}
      title="Save this list as a view"
      description="Keeps the filter, search, sort order and columns you are looking at now."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="save-view-form" loading={create.isPending}>
            Save view
          </Button>
        </>
      }
    >
      <form id="save-view-form" onSubmit={submit} noValidate className="space-y-3">
        <Field label="Name" htmlFor="view-name" required error={error ?? undefined}>
          <Input
            id="view-name"
            value={name}
            maxLength={120}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'view-name-error' : undefined}
            placeholder="Network – open"
          />
        </Field>
        <Field label="Description" htmlFor="view-description" hint="Optional. Shown under the name in the Views menu.">
          <Textarea
            id="view-description"
            value={description}
            maxLength={500}
            rows={2}
            className="min-h-0"
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        {canPersonal && canShared && (
          <fieldset className="space-y-1.5">
            <legend className="text-mini font-medium text-ink-muted">Who can see it</legend>
            <label className="flex items-center gap-2 text-body text-ink">
              <input
                type="radio"
                name="view-visibility"
                checked={visibility === 'private'}
                onChange={() => setVisibility('private')}
                className="accent-[var(--color-accent)]"
              />
              Only me
            </label>
            <label className="flex items-center gap-2 text-body text-ink">
              <input
                type="radio"
                name="view-visibility"
                checked={visibility === 'shared'}
                onChange={() => setVisibility('shared')}
                className="accent-[var(--color-accent)]"
              />
              Everyone on the desk
            </label>
          </fieldset>
        )}
        {!canPersonal && canShared && (
          <p className="text-mini text-ink-muted">Views you save are shared with everyone on the desk.</p>
        )}
        <label className="flex items-center gap-2 text-body text-ink">
          <input
            type="checkbox"
            checked={pinIt}
            onChange={(event) => setPinIt(event.target.checked)}
            className="size-3.5 accent-[var(--color-accent)]"
          />
          Pin it to the tabs above the list
        </label>
      </form>
    </Dialog>
  );
}

/** The visibility a PATCH must restate, or the server resets it to private. */
function storedVisibility(view: SavedView): 'private' | 'shared' {
  return view.visibility === 'private' ? 'private' : 'shared';
}

export function RenameViewDialog({ view, onClose }: { view: SavedView; onClose: () => void }) {
  const toast = useToast();
  const { update } = useViewMutations();
  const [name, setName] = useState(view.name);
  const [description, setDescription] = useState(view.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!trimmed) {
      setError('A view needs a name.');
      return;
    }
    setError(null);
    update.mutate(
      {
        id: view.id,
        changes: { name: trimmed, description: description.trim() || null, visibility: storedVisibility(view) },
      },
      {
        onSuccess: () => {
          toast.success(`Renamed to “${trimmed}”`);
          onClose();
        },
        onError: (failure) => {
          const inline = nameError(failure, trimmed);
          if (inline) setError(inline);
          else toast.error(failure);
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`Rename “${view.name}”`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="rename-view-form" loading={update.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="rename-view-form" onSubmit={submit} noValidate className="space-y-3">
        <Field label="Name" htmlFor="rename-view-name" required error={error ?? undefined}>
          <Input
            id="rename-view-name"
            value={name}
            maxLength={120}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'rename-view-name-error' : undefined}
          />
        </Field>
        <Field label="Description" htmlFor="rename-view-description">
          <Textarea
            id="rename-view-description"
            value={description}
            maxLength={500}
            rows={2}
            className="min-h-0"
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </form>
    </Dialog>
  );
}

/** Writes what the list shows now into the view it started from. */
export function useSaveViewChanges() {
  const toast = useToast();
  const confirm = useConfirm();
  const { update } = useViewMutations();
  const save = async (view: SavedView, snapshot: ViewSnapshot, onSaved: () => void) => {
    // Leaving the filter out of the update keeps the stored one, so a view
    // whose conditions were all removed would come back unchanged under a
    // toast saying the changes were saved. The list offers a new view instead.
    if (!snapshot.filter && asFilterNode(view.filter)) {
      toast.error('A view’s conditions can be changed, not all removed. Save this as a new view instead.');
      return;
    }
    // A shared view is somebody else's queue too, and there is no undo: one
    // click on "Save changes" used to rewrite it for the whole desk.
    if (view.visibility !== 'private') {
      const ok = await confirm({
        title: `Save changes to “${view.name}”?`,
        description: view.is_system
          ? 'This is one of the built-in views. Everyone on the desk who uses it will see the list as it is now.'
          : 'It is shared, so everyone who uses it will see the list as it is now.',
        confirmLabel: 'Save changes',
      });
      if (!ok) return;
    }
    update.mutate(
      {
        id: view.id,
        changes: {
          visibility: storedVisibility(view),
          ...(snapshot.filter ? { filter: snapshot.filter } : {}),
          sort: snapshot.sort,
          search_query: snapshot.searchQuery,
          columns: snapshot.columns,
        },
      },
      {
        onSuccess: () => {
          toast.success(`Saved changes to “${view.name}”`);
          onSaved();
        },
        onError: (failure) => toast.error(failure),
      },
    );
  };
  return { save, pending: update.isPending };
}
